import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { fetchFsEvents, type FsEventItem } from '../lib/fsEvents';
import type { FsEventType } from '../lib/watchedPaths';

const ALL_EVENT_TYPES: readonly FsEventType[] = ['created', 'modified', 'deleted', 'renamed'];

const EVENT_COLOR: Record<FsEventType, 'success' | 'info' | 'error' | 'warning'> = {
  created: 'success',
  modified: 'info',
  deleted: 'error',
  renamed: 'warning',
};

function formatBytes(n: number | null): string {
  if (n === null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function AgentEventsPage(): JSX.Element {
  const { agentId } = useParams<{ agentId: string }>();
  const [selectedTypes, setSelectedTypes] = useState<FsEventType[]>([]);
  const [pathPrefix, setPathPrefix] = useState<string>('');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [cursor, setCursor] = useState<number>(0);
  const [pages, setPages] = useState<FsEventItem[][]>([]);

  const eventsQuery = useQuery({
    queryKey: ['fsEvents', agentId, selectedTypes, pathPrefix, from, to, cursor],
    queryFn: () => {
      const q: Record<string, unknown> = { limit: 100 };
      if (selectedTypes.length > 0) q.eventType = selectedTypes;
      if (pathPrefix !== '') q.pathPrefix = pathPrefix;
      if (from !== '') q.from = from;
      if (to !== '') q.to = to;
      if (cursor > 0) q.cursor = cursor;
      return fetchFsEvents(agentId ?? '', q as Parameters<typeof fetchFsEvents>[1]);
    },
    enabled: agentId !== undefined,
  });

  function toggleType(et: FsEventType): void {
    setCursor(0);
    setPages([]);
    setSelectedTypes((prev) =>
      prev.includes(et) ? prev.filter((e) => e !== et) : [...prev, et]
    );
  }

  function applyFilters(): void {
    setCursor(0);
    setPages([]);
    void eventsQuery.refetch();
  }

  function loadMore(): void {
    if (eventsQuery.data?.nextCursor) {
      const snapshot = [...eventsQuery.data.items];
      setPages((prev) => [...prev, snapshot]);
      setCursor(eventsQuery.data.nextCursor);
    }
  }

  if (agentId === undefined) {
    return <Alert severity="error">agentId lipsă din URL.</Alert>;
  }

  const items = [...pages.flat(), ...(eventsQuery.data?.items ?? [])];

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Filesystem events — agent {agentId.slice(0, 8)}…
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            {ALL_EVENT_TYPES.map((et) => (
              <FormControlLabel
                key={et}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedTypes.includes(et)}
                    onChange={() => toggleType(et)}
                  />
                }
                label={et}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Path prefix"
              value={pathPrefix}
              onChange={(e) => setPathPrefix(e.target.value)}
              size="small"
              fullWidth
              placeholder="C:\Confidential"
            />
            <TextField
              label="From (YYYY-MM-DD HH:MM:SS)"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="To (YYYY-MM-DD HH:MM:SS)"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              size="small"
              fullWidth
            />
            <Button onClick={applyFilters} variant="contained" size="small">
              Aplică
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {eventsQuery.isLoading && <CircularProgress />}
      {eventsQuery.isError && <Alert severity="error">Eroare la încărcare events.</Alert>}

      {eventsQuery.data && (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Captured at</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Watched path</TableCell>
                <TableCell>File path</TableCell>
                <TableCell>New path</TableCell>
                <TableCell align="right">Size</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((ev) => (
                <TableRow key={ev.id}>
                  <TableCell>{ev.id}</TableCell>
                  <TableCell>{ev.capturedAt}</TableCell>
                  <TableCell>
                    <Chip label={ev.eventType} size="small" color={EVENT_COLOR[ev.eventType]} />
                  </TableCell>
                  <TableCell>{ev.watchedPath}</TableCell>
                  <TableCell>{ev.filePath}</TableCell>
                  <TableCell>{ev.newPath ?? '—'}</TableCell>
                  <TableCell align="right">{formatBytes(ev.fileSize)}</TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      Niciun event pentru filtrele curente.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {eventsQuery.data.nextCursor !== null && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Button onClick={loadMore} size="small">
                Load more
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
